import React, { useState } from 'react';
import { Form, Input, DatePicker, Select, Upload, message, Checkbox, Card } from 'antd';
import { UserOutlined, IdcardOutlined, CameraOutlined, CheckCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { UPLOAD_USER_IMAGE_URL } from '../../../../Constants/Api/azure/azure';

const { Option } = Select;

const Section01Personal = ({ form, profilePhoto, setProfilePhoto, userId }) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleCustomUpload = async ({ file, onSuccess, onError }) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      if (userId) {
        formData.append('fileName', `avatar_${userId}`);
      }

      const response = await fetch(UPLOAD_USER_IMAGE_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        let errMsg = `Upload failed (${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.message) errMsg = errData.message;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const data = await response.json();
      if (data && data.url) {
        setProfilePhoto(data.url);
        form.setFieldsValue({ profilePhotoUrl: data.url });
        message.success('Profile photo uploaded successfully');
        onSuccess(data, file);
      } else {
        throw new Error(data?.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Profile photo upload error:', err);
      message.error(err.message || 'Failed to upload photo');
      onError(err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <span className="section-badge">SECTION 01</span>
        <h3>Personal Information</h3>
        <p className="section-description">
          Please enter your official identification details. Ensure your name matches your national ID or passport.
        </p>
      </div>

      <div className="form-grid">
        <Form.Item
          label="Full Legal Name (As in NIC / Passport)"
          name="fullName"
          rules={[{ required: true, message: 'Please enter your full legal name' }]}
        >
          <Input prefix={<UserOutlined />} placeholder="e.g. Kasun Chamara Perera" size="large" />
        </Form.Item>

        {/* Highlighted Name to Appear on Certificate */}
        <Card className="certificate-name-card" bordered={false}>
          <div className="cert-card-badge">
            <CheckCircleFilled style={{ color: '#d97706', marginRight: 6 }} />
            CRITICAL FOR CERTIFICATE PRINTING
          </div>
          <Form.Item
            label={<strong style={{ fontSize: '1.02rem', color: '#92400e' }}>Name to Appear on Certificate *</strong>}
            name="certificateName"
            rules={[{ required: true, message: 'Please enter the exact name for certificate printing' }]}
            extra="This name will be embossed on your official graduation certificate and digital verification credentials."
          >
            <Input
              prefix={<IdcardOutlined />}
              placeholder="e.g. K. C. Perera"
              size="large"
              className="cert-name-input"
            />
          </Form.Item>

          <Form.Item
            name="certificateNameConfirmed"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('Please confirm your certificate name')),
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Checkbox>
              <span style={{ fontWeight: 600, color: '#78350f' }}>
                I confirm that the above name is correct and should be printed on my official certificate.
              </span>
            </Checkbox>
          </Form.Item>
        </Card>

        <div className="grid-two-cols">
          <Form.Item
            label="Date of Birth"
            name="dateOfBirth"
            rules={[{ required: true, message: 'Please select your date of birth' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              size="large"
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current > dayjs().subtract(10, 'years')}
              placeholder="Select birth date"
            />
          </Form.Item>

          <Form.Item label="Gender" name="gender">
            <Select size="large" placeholder="Select gender (optional)">
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
              <Option value="other">Other</Option>
              <Option value="prefer_not_to_say">Prefer not to say</Option>
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          label="NIC or Passport Number"
          name="nicPassport"
          rules={[{ required: true, message: 'Please enter your NIC or passport number' }]}
          extra="Sri Lankan NIC (old 9 digits + V/X or new 12 digits) or International Passport Number"
        >
          <Input prefix={<IdcardOutlined />} placeholder="e.g. 199512345678 or 951234567V / N1234567" size="large" />
        </Form.Item>

        <Form.Item label="Profile Photograph" name="profilePhotoUrl" extra="Upload a clear front-facing passport photograph (JPG/PNG, max 5MB)">
          <div className="avatar-uploader-row">
            <div className="avatar-preview-box">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="avatar-preview-img" />
              ) : (
                <div className="avatar-placeholder">
                  <UserOutlined style={{ fontSize: 32, color: '#9ca3af' }} />
                </div>
              )}
            </div>
            <div className="avatar-upload-action">
              <Upload
                accept="image/png,image/jpeg,image/webp"
                customRequest={handleCustomUpload}
                showUploadList={false}
                disabled={isUploading}
              >
                <button type="button" className="custom-upload-btn" disabled={isUploading}>
                  <CameraOutlined style={{ marginRight: 6 }} />
                  {isUploading ? 'Uploading...' : profilePhoto ? 'Change Photo' : 'Upload Photograph'}
                </button>
              </Upload>
              <p className="upload-hint">Accepted formats: JPG, PNG, WEBP. Maximum size 5MB.</p>
            </div>
          </div>
        </Form.Item>
      </div>
    </div>
  );
};

export default Section01Personal;
